import { useDraggable } from "@dnd-kit/core";
import { AlertTriangle, Calendar, CalendarClock, Package, Stethoscope, User } from "lucide-react";
import { cn } from "@/lib/cn";
import { codigoDoServico } from "@/lib/codigo-do-pedido";
import { formatDate } from "@/components/prosthesis/prosthesis-meta";
import { situacaoDoServico, serviceLabel, type ServiceRow } from "./production-types";
import { SeloVenceEmBreve } from "./PrazoComAtraso";
import { prazoEfetivo, type PrazoDoLaboratorio } from "@bookdental/schemas";

export function ProductionKanbanCard({
  row,
  dragging,
  prazo,
  arrastavel = true,
  onOpenService,
}: {
  row: ServiceRow;
  dragging?: boolean;
  prazo: PrazoDoLaboratorio;
  /** Sem `production.manage` o card continua abrindo, mas não arrasta — mover entre estágios é
   *  escrita, e o servidor recusaria. */
  arrastavel?: boolean;
  onOpenService?: (row: ServiceRow) => void;
}) {
  const { order, service } = row;
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: service.id,
    data: { type: "card", row },
    disabled: !arrastavel,
  });

  const cancelled = service.status === "CANCELADO";
  const situacao = cancelled ? "noPrazo" : situacaoDoServico(row, prazo);
  const overdue = situacao === "atrasado";

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-service-id={service.id}
      onClick={() => onOpenService?.(row)}
      className={cn(
        "rounded-lg border p-3 transition-shadow",
        dragging
          ? "cursor-grabbing shadow-lg"
          : arrastavel
            ? "cursor-grab shadow-sm hover:shadow-md"
            : "cursor-pointer shadow-sm hover:shadow-md",
        overdue && !cancelled
          ? "border-red-300 bg-red-50/60 dark:border-red-900/50 dark:bg-red-950/10"
          : "border-zinc-200 bg-white dark:border-border dark:bg-card",
        cancelled && "opacity-50",
      )}
      style={
        transform && !dragging
          ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">
            <span className="mr-1.5 font-mono text-[11px] font-semibold text-zinc-400 dark:text-muted-foreground">#{codigoDoServico(order, service)}</span>
            {order.patientName}
          </p>
          <p className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
            {order.organization.isPersonal ? "" : order.organization.name}
          </p>
        </div>
        {service.hasOpenRework && (
          <span
            title="Retrabalho em aberto"
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400"
          >
            <AlertTriangle className="h-3 w-3" />
          </span>
        )}
      </div>

      {order.dentistName && (
        <div className="mt-2 flex items-center gap-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
          <Stethoscope className="h-3 w-3" />
          {order.dentistName}
        </div>
      )}

      <div className="mt-1 flex items-center gap-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
        <Calendar className="h-3 w-3" />
        Pedido: {formatDate(order.createdAt)}
      </div>

      {(order.deadline || situacao !== "noPrazo") && (
        <div
          className={cn(
            "mt-1 flex items-center gap-1 text-[11px]",
            overdue
              ? "font-semibold text-red-600 dark:text-red-400"
              : situacao === "venceEmBreve"
                ? "font-semibold text-amber-600 dark:text-amber-400"
                : "text-zinc-500 dark:text-muted-foreground",
          )}
        >
          <CalendarClock className="h-3 w-3" />
          Prazo: {formatDate(order.deadline ?? prazoEfetivo(order, prazo))}
          {situacao === "venceEmBreve" && <span className="ml-auto"><SeloVenceEmBreve compacto /></span>}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1">
        <Package className="h-3 w-3 shrink-0 text-zinc-400 dark:text-muted-foreground" />
        <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:bg-primary/10 dark:text-primary">
          {serviceLabel(service)}
        </span>
        {service.productionStage && (
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{
              backgroundColor: `${service.productionStage.colorHex ?? "#2457ff"}1a`,
              color: service.productionStage.colorHex ?? "#2457ff",
            }}
          >
            {service.productionStage.label}
          </span>
        )}
      </div>

      {service.assignedToUser && (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
          <User className="h-3 w-3" />
          {service.assignedToUser.name ?? "Colaborador"}
        </div>
      )}
    </div>
  );
}
